import { FieldModel, FieldQueryFilterName, FieldQueryOptions, UserId } from '@cibo/core'
import { GridRowId } from '@mui/x-data-grid-pro'
import { useQueries } from '@tanstack/react-query'
import { uniq } from 'ramda'
import { createContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QUERY_KEY } from '../../queries'
import { useLandManagement } from '../LandManagementProvider'

export const PAGE_SIZES = [10, 25, 50]

export interface PaginatedFieldsState {
  // these convenience functions translate from table-row spec to resourceIds
  onDelete(fieldId?: string): void
  onLabel(): void
  onAssignFields(): void
  onAddInformation(): void

  selectedFields: FieldModel[]
  ownedByUsers: UserId[]
  selectedRowIds: GridRowId[]
  setSelectedRowIds(ids: GridRowId[]): void
  deselectRowIds(ids: GridRowId[]): void
  clearSelectedRowIds(): void

  page: number
  setPage(page: number): void
  pageSize: number
  setPageSize(page: number): void
  filters: FieldQueryOptions
  setFilters: (filters: FieldQueryOptions) => void
  hideFilters: Partial<Record<FieldQueryFilterName, boolean>> & {
    fdrStatusAnyOf?: boolean
  }

  isReady: boolean
}

export const PaginatedFieldsContext = createContext<PaginatedFieldsState>({
  filters: {},
  setFilters: console.log,
  hideFilters: {},
  selectedFields: [],
  ownedByUsers: [],
  selectedRowIds: [],
  setSelectedRowIds: console.log,
  deselectRowIds: console.log,
  clearSelectedRowIds: console.log,

  onDelete: console.log,
  onLabel: console.log,
  onAssignFields: console.log,
  onAddInformation: console.log,

  page: 0,
  setPage: console.log,
  pageSize: 10,
  setPageSize: console.log,
  isReady: false,
})

type PaginatedFieldsProviderProps = {
  hideFilters?: Partial<Record<FieldQueryFilterName, boolean>>
}

export const PaginatedFieldsProvider: React.FC<PaginatedFieldsProviderProps> = ({
  children,
  hideFilters = {},
}) => {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<FieldQueryOptions>({})

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
  const clearSelectedRowIds = () => setSelectedRowIds([])

  const queries = useQueries({
    queries: selectedRowIds
      ? selectedRowIds.map(id => ({
          queryKey: [QUERY_KEY.FIELDS_PAGINATED_BY_RESOURCE_ID, id],
        }))
      : [],
  })

  const { setDeleteModal, setLabelModal, setAssignFieldsModal, LAND_MANAGER_ROUTES } =
    useLandManagement()

  const handleSetSelectedRowIds = (ids: GridRowId[]) => {
    setSelectedRowIds(ids as string[])
  }
  const handleDeselectRowIds = (ids: GridRowId[]) => {
    setSelectedRowIds(selectedRowIds.filter(id => !ids.includes(id)))
  }

  const selectedFields = queries.map(query => query.data as FieldModel)

  const ownedByUsers = uniq(selectedFields.filter(Boolean).map(f => f.ownedBy?.userId))

  return (
    <PaginatedFieldsContext.Provider
      value={{
        filters,
        setFilters,
        hideFilters,

        isReady: !queries.some(query => query.isLoading),

        selectedFields,
        ownedByUsers,
        selectedRowIds,
        setSelectedRowIds: handleSetSelectedRowIds,
        deselectRowIds: handleDeselectRowIds,
        clearSelectedRowIds,

        page,
        setPage,

        pageSize,
        setPageSize,

        onDelete: (fieldId?: string) =>
          setDeleteModal({
            open: true,
            selectedIds: fieldId ? [fieldId] : selectedRowIds,
            onSuccess: clearSelectedRowIds,
          }),

        onLabel: () => setLabelModal({ open: true, selectedIds: selectedRowIds }),
        onAssignFields: () => setAssignFieldsModal({ open: true, selectedIds: selectedRowIds }),
        onAddInformation: () =>
          navigate(LAND_MANAGER_ROUTES.PROFILE_FIELDS, {
            state: { profileFieldIds: selectedRowIds },
          }),
      }}
    >
      {children}
    </PaginatedFieldsContext.Provider>
  )
}
